import { SubmitClaim } from "commonAssets/components/Forms/SubmitClaim";
import { withLoginRecaptcha } from "../loginRecaptcha.js";
import { createElement } from "commonAssets/lib/createElement";
import { loadingGif } from "commonAssets/lib/loadingGif";
import { modal } from "commonAssets/lib/modal";
import { validateField } from "commonAssets/lib/validateField";
import { clearMessage } from "commonAssets/lib/clearMessage";
import { toggleFieldError } from "commonAssets/lib/toggleFieldError";
import { toggleFieldWarning } from "commonAssets/lib/toggleFieldWarning";
import { uniformClaimDeadline } from "commonAssets/lib/uniformClaimDeadline";
import { localDatetime } from "commonAssets/lib/localDatetime";
import { ConfirmEmailAddress } from "commonAssets/lib/ConfirmEmailAddress";
import {
    i18n,
    loading,
    outputDiv,
    reCAPTCHAv3,
    appPayMethod,
} from "commonAssets/js/constants";
import { dstProcess } from "commonAssets/components/FormBase/DigitalDisbursements";
import { ErrorHandler } from "commonAssets/utils/ErrorHandler";
import formDefault from "../../docs/formDefault.json";

function getCmDataSubclasses(resultData) {
    const cmDataRaw =
        resultData?.class_member?.cm_data ?? resultData?.cm_data ?? null;
    if (!cmDataRaw) return [];

    let cmData = cmDataRaw;
    if (typeof cmData === "string") {
        try {
            cmData = JSON.parse(cmData);
        } catch {
            return [];
        }
    }

    const subclass = cmData?.subclass;
    if (Array.isArray(subclass)) {
        return subclass
            .filter((s) => typeof s === "string")
            .map((s) => s.toLowerCase());
    }
    if (typeof subclass === "string") return [subclass.toLowerCase()];
    return [];
}

export class customSubmitClaim extends SubmitClaim {
    constructor(...args) {
        super(...args);
        // Class member logins (getClassMember) require a reCAPTCHA token.
        withLoginRecaptcha(this.claim.api);
    }

    async form() {
        const form = formDefault.claim_form[this.lang];
        this.init_claim(form);
    }

    init_claim(form) {
        this.claim.api
            .claimsDeadlineGet({ domain: this.claim.data.domain })
            .then((result) => {
                // Claim Deadline - Default Pacific DateTime
                let datetime;
                if (Boolean(result.data.pacific_datetime)) {
                    datetime = result.data.pacific_datetime;
                } else {
                    datetime = `<pre>Error: datetime</pre>`;
                }

                let claim_deadline;
                if (Boolean(result.data.claim_deadline)) {
                    claim_deadline = result.data.claim_deadline;
                }

                // Error Handling
                if (result.data.error) {
                    this.setup_result_data_error(result);
                    return;
                }

                const container = createElement("div", {
                    class: "display-form",
                });

                /**
                 * Claim Guard
                 */
                if (form.form_options.notice_confirmation.display === true) {
                    // Guard
                    container.appendChild(this.guard(form, datetime));
                    outputDiv.appendChild(container);

                    const zero_zero_id =
                        document.getElementById("zero_zero_id");
                    const confirmation_code =
                        document.getElementById("confirmation_code");
                    const skip_guard = document.getElementById("skip-guard");

                    // Notice Id and Confirmation Code
                    [zero_zero_id, confirmation_code].forEach(function (input) {
                        input.addEventListener("change", validateField);
                    });

                    // Skip Guard
                    if (skip_guard) {
                        skip_guard.addEventListener("click", () => {
                            // Hide guard
                            // document.getElementById('guard').classList.add("d-none");
                            document.getElementById("guard").remove();
                            // New Claim Form
                            container.appendChild(
                                this.claim_form(form, datetime)
                            );
                            container.scrollIntoView({ behavior: "smooth" });
                            outputDiv.appendChild(container);

                            // Init claim fields
                            this.init_claim_fields(
                                this.claim,
                                form,
                                claim_deadline
                            );
                        });
                    }

                    // ada compliance updates - added
                    // if input#submit-guard was clicked add .was-verified to form#guard
                    document
                        .getElementById("submit-guard")
                        .addEventListener("click", () => {
                            document
                                .getElementById("guard")
                                .classList.add("was-validated");
                            // TODO: add required text to labels
                        });

                    // Validate Guard Fields
                    document
                        .getElementById("guard")
                        .addEventListener("submit", (e) => {
                            e.preventDefault();

                            let valid = true;
                            const member = {
                                domain: this.claim.data.domain,
                                zero_zero_id: zero_zero_id.value.toUpperCase(),
                                confirmation_code: confirmation_code.value,
                            };

                            clearMessage();

                            // Validate guard fields
                            [zero_zero_id, confirmation_code].forEach(
                                function (input) {
                                    valid = validateField(input) && valid;
                                }
                            );
                            if (!valid) return;

                            loadingGif(loading, "show");

                            // Retrieve class member data (use POST to pass notice id and confirmation code privately)
                            this.claim.api
                                .classMemberPost({}, member)
                                .then((result) => {
                                    // Check for error message
                                    if (result.data.error) {
                                        toggleFieldError(
                                            zero_zero_id,
                                            true,
                                            ""
                                        );
                                        toggleFieldError(
                                            confirmation_code,
                                            true,
                                            ""
                                        );
                                        this.errorHandler.log_exception(
                                            new Error(
                                                "Error loading class member",
                                                { cause: result }
                                            )
                                        );
                                        this.errorHandler.display_modal(result);
                                        return;
                                    }

                                    // Add notice id and confirmation code to claim data
                                    this.claim.data.zero_zero_id =
                                        member.zero_zero_id;
                                    this.claim.data.confirmation_code =
                                        member.confirmation_code;

                                    // Define the environment for the OTP
                                    const environment =
                                        this.claim.data.domain.split(".")[0]; // ada compliance - added

                                    // Guard gating via cm_data.subclass:
                                    // - claim_id response may include `class_member.cm_data`
                                    // - non-claim_id response may include `cm_data`
                                    const subclasses =
                                        getCmDataSubclasses(result.data);
                                    const requiredSubclasses = [
                                        "class_member",
                                        "claim_form",
                                    ];
                                    const hasAccess = requiredSubclasses.every(
                                        (key) => subclasses.includes(key)
                                    );
                                    if (!hasAccess) {
                                        loadingGif(loading, "hide", 250);
                                        modal(
                                            i18n.getTranslation("error"),
                                            `<p>This Notice ID is not eligible to submit a Claim Form.</p><p>If you believe this is an error, contact the Settlement Administrator at <a href="mailto:Info@2025PriceSettlement.com">Info@2025PriceSettlement.com</a>.</p>`
                                        );
                                        return;
                                    }

                                    if (result.data.claim_id) {
                                        this.claim.data.claim_id =
                                            result.data.claim_id;
                                        this.claim.class_member = result.data;
                                        if (environment !== "local") {
                                            this.send_otp(
                                                form,
                                                container,
                                                result.data,
                                                claim_deadline
                                            );
                                            return;
                                        }
                                    }

                                    // Populate claim form
                                    this.populate_claim_form(
                                        form,
                                        container,
                                        result.data,
                                        claim_deadline
                                    );
                                })
                                .catch((error) => {
                                    this.errorHandler.log_exception(
                                        new Error(
                                            "Error loading class member",
                                            { cause: error }
                                        )
                                    );
                                    this.errorHandler.display_modal(error);
                                });
                        });
                } else {
                    /**
                     * No Claim Guard
                     */
                    // Edit claim link
                    this.edit_claim(form);

                    // New Claim Form
                    const deadlineSpan = uniformClaimDeadline(claim_deadline);
                    container.appendChild(this.claim_form(form, deadlineSpan));
                    outputDiv.appendChild(container);

                    // Init claim fields
                    this.init_claim_fields(this.claim, form, claim_deadline);
                }

                loadingGif(loading, "hide", 250);
            })
            .catch((error) => {
                this.errorHandler.log_exception(
                    new Error("Error fetching claim deadline", { cause: error })
                );
                this.errorHandler.display(error);
            });
    }

    async process_claim(e, claim, deadline) {
        console.log("[process_claim]");

        e.preventDefault();

        document.getElementById("submit-claim").disabled = true;

        if (claim.flags.sent) {
            return;
        }

        clearMessage();

        claim.flags.valid = true;
        claim.data.fields = {};

        this.init_claim_inputs();

        this.format_claim_data(claim);

        claim.data.contact.date = localDatetime();

        if (document.getElementById("documentation")) {
            this.process_claim_files(this.claim);
        }

        if (
            document.getElementById("email_address") &&
            document.getElementById("confirm_email_address")
        ) {
            ConfirmEmailAddress("process", this.claim);
        }

        if (
            appPayMethod &&
            appPayMethod !== "" &&
            this.customDstProcess === null
        ) {
            claim = dstProcess(this.claim);
        } else if (
            appPayMethod &&
            appPayMethod !== "" &&
            this.customDstProcess !== null
        ) {
            claim = this.customDstProcess(this.claim);
        }

        const s3env = this.config.url;

        // Custom process claim with async support
        if (this.customProcessClaim != null) {
            try {
                const maybeClaim = this.customProcessClaim(this.claim);
                claim =
                    maybeClaim && typeof maybeClaim.then === "function"
                        ? await maybeClaim
                        : maybeClaim;
            } catch (error) {
                claim.flags.valid = false;
                this.errorHandler.log_exception(
                    new Error("Error in custom process claim", { cause: error })
                );
                this.errorHandler.display_modal(error);
            }
        }

        if (claim.flags.valid) {
            try {
                const token = await grecaptcha.enterprise.execute(reCAPTCHAv3, {
                    action: "submit",
                });
                claim.data.token = token;
                this.post_claim(claim, s3env);
            } catch (error) {
                document.getElementById("submit-claim").disabled = false;
                this.errorHandler.log_exception(
                    new Error("Error retrieving reCAPTCHA token", {
                        cause: error,
                    })
                );
                this.errorHandler.display_modal(error);
            }
        } else {
            document.getElementById("submit-claim").disabled = false;
            if (!claim.flags.suppress_error_modal) {
                modal(
                    i18n.getTranslation("error"),
                    i18n.getTranslation("claim_form_errors")
                );
            }
        }
    }

    static createInstance({
        appClaimForm,
        userLanguage,
        customInitClaim = null,
        customProcessClaim = null,
        customLoadMember = null,
        customDstProcess = null,
        errorHandler = new ErrorHandler(),
    }) {
        return new customSubmitClaim(
            appClaimForm,
            userLanguage,
            customInitClaim,
            customProcessClaim,
            customLoadMember,
            customDstProcess,
            errorHandler
        );
    }
}
